import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useTheme } from "@/context/ThemeContext";
import { useTextColorClass } from "@/components/ui/text-coloring";
import { useLanguage } from "@/context/LanguageContext";
import { carDetailsLabels } from "@/i18n";

interface AdditionalCommentsProps {
  comments?: string;
  /** When false, render content expanded (no accordion), e.g. for document/PDF view. */
  collapsible?: boolean;
}

const AdditionalComments = ({
  comments,
  collapsible = true,
}: AdditionalCommentsProps) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { baseText, subtleText } = useTextColorClass();
  const { language } = useLanguage();
  const labels = carDetailsLabels(language).additionalComments;

  if (!comments) {
    return null;
  }

  const content = (
    <div
      className={`p-2 sm:p-3 ${
        isDark ? "bg-black/20" : "bg-gray-100"
      } rounded-lg`}
    >
      <p className={`${subtleText} text-base sm:text-base whitespace-pre-wrap`}>
        {comments}
      </p>
    </div>
  );

  if (!collapsible) {
    return (
      <div
        className={`rounded-lg backdrop-blur border bg-card text-card-foreground shadow-sm animate-fade-in`}
      >
        <div className="p-4 md:p-6">
          <h2 className={`font-semibold text-sm md:text-base mb-4 ${baseText}`}>
            {labels.title}
          </h2>
          {content}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-lg backdrop-blur border bg-card text-card-foreground shadow-sm animate-fade-in`}
    >
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="additional-comments">
          <AccordionTrigger
            className={`px-4 py-3 hover:bg-accent/20 flex justify-between`}
          >
            <span className="font-semibold text-base">{labels.title}</span>
          </AccordionTrigger>
          <AccordionContent className={`p-4`}>{content}</AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default AdditionalComments;
